/**
 * WHAT A SHOPPER DOES INSIDE THE CHECKOUT FORM, FIELD BY FIELD — and nothing they type.
 *
 * Every one of the 18 shoppers who opened DK's checkout in the week to 23 September 2026 left on
 * the delivery step, and all that was kept of the step was that they were on it. Which field they
 * were in, how long, whether they left it empty, which field turned them back, what device they
 * held — none of it. These helpers name that, for the checkout page to send through the shop's
 * batched analytics (`checkout_field`, `checkout_left`, `checkout_env`, `checkout_blocked`).
 *
 * NOTHING PERSONAL LEAVES: a field's NAME, seconds, a count of characters in the address search,
 * whether a value is there — never the value. The server shows the trace through an allow-list
 * (stylon-server ads/journeys/checkout-trace.core.ts) that refuses a name, phone or address key
 * whatever a payload says.
 */

/** Element ids / test ids → the field's name in the trace. Order matters: first match wins. */
const FIELD_NAMES = [
  ['co-name', 'name'], ['co-email', 'email'], ['co-phone', 'phone'],
  ['checkout-address-input', 'address_search'], ['checkout-address-street', 'street'], ['checkout-address-city', 'city'],
  ['checkout-address-building', 'building'], ['checkout-address-apt', 'apt'], ['checkout-address-floor', 'floor'],
  ['checkout-address-note', 'courier_note'], ['checkout-order-note', 'order_note'], ['co-invoice-type', 'invoice_type'],
  ['co-company-name', 'company'], ['co-vat-number', 'vat'], ['co-national-id', 'national_id'],
  ['co-billing-name', 'billing_name'], ['co-billing-surname', 'billing_surname'], ['co-billing-country', 'billing_country'],
  ['co-billing-province', 'billing_province'], ['co-billing-district', 'billing_district'],
  ['co-billing-postal', 'billing_postal'], ['co-billing-address', 'billing_address'],
];
const FORM_TAGS = new Set(['INPUT', 'SELECT', 'TEXTAREA']);
const NAME_RE = /^[a-z0-9_]{1,40}$/;

/** The trace name of a focused element, or null when it is not a field of the form. */
export function fieldOfTarget(el) {
  if (!el || !FORM_TAGS.has(String(el.tagName || '').toUpperCase())) return null;
  const type = String(el.type || '').toLowerCase();
  if (type === 'hidden' || type === 'submit' || type === 'button') return null;
  const id = String(el.id || '');
  const testId = String(el.getAttribute ? el.getAttribute('data-testid') || '' : '');
  for (const [key, name] of FIELD_NAMES) if (id === key || testId === key) return name;
  if (type === 'tel') return 'phone';
  if (type === 'checkbox' || type === 'radio') return `${type}:${(id || testId || 'other').replace(/^(co-|checkout-)/, '')}`.slice(0, 40);
  const guess = (id || testId).replace(/^(co-|checkout-)/, '').replace(/-/g, '_').toLowerCase();
  return NAME_RE.test(guess) ? guess : 'other';
}

/** Whether a field holds anything — a yes or a no, never what. */
export function isFilled(el) {
  if (!el) return false;
  const type = String(el.type || '').toLowerCase();
  if (type === 'checkbox' || type === 'radio') return !!el.checked;
  return String(el.value ?? '').trim().length > 0;
}

/** How many characters a field holds (the address search only ever reports this). */
export function lengthOf(el) {
  return el ? String(el.value ?? '').length : 0;
}

/** The device, browser, screen and language of the shopper — the class of device, not the device. */
export function envOf(nav = typeof navigator !== 'undefined' ? navigator : {}, win = typeof window !== 'undefined' ? window : {}) {
  const ua = String(nav.userAgent || '');
  const os = /iPhone|iPad|iPod/.test(ua) ? 'ios' : /Android/.test(ua) ? 'android' : /Windows/.test(ua) ? 'windows' : /Mac OS X/.test(ua) ? 'mac' : /Linux/.test(ua) ? 'linux' : 'other';
  const browser = /EdgA?\//.test(ua) ? 'edge' : /SamsungBrowser/.test(ua) ? 'samsung' : /OPR\//.test(ua) ? 'opera'
    : /Chrome\//.test(ua) && !/Chromium/.test(ua) ? 'chrome' : /Safari\//.test(ua) && /Version\//.test(ua) ? 'safari' : /Firefox\//.test(ua) ? 'firefox' : 'other';
  const inApp = /FBAN|FBAV|Instagram|Line\/|Twitter|TikTok|Snapchat/.test(ua);
  const conn = nav.connection && typeof nav.connection === 'object' ? String(nav.connection.effectiveType || '') : '';
  let standalone;
  try { standalone = !!(nav.standalone || (win.matchMedia && win.matchMedia('(display-mode: standalone)').matches)); } catch (_) { standalone = undefined; }
  let uiLang = '';
  try { uiLang = String((typeof document !== 'undefined' && document.documentElement && document.documentElement.lang) || '').slice(0, 8); } catch (_) { uiLang = ''; }
  let tz;
  try { tz = new Date().getTimezoneOffset(); } catch (_) { tz = undefined; }
  return {
    // `handheld`, not `mobile`: the trace's allow-list refuses any key that could name a phone number, and "mobile" is one.
    handheld: /Mobi|Android|iPhone|iPad/.test(ua),
    os,
    browser: inApp ? `${browser}:inapp` : browser,
    w: Number(win.innerWidth) || 0,
    h: Number(win.innerHeight) || 0,
    dpr: Math.round((Number(win.devicePixelRatio) || 1) * 10) / 10,
    lang: String(nav.language || '').slice(0, 8),
    ...(uiLang ? { uiLang } : {}),
    ...(tz !== undefined ? { tz } : {}),
    ...(conn ? { conn: conn.slice(0, 8) } : {}),
    ...(standalone !== undefined ? { standalone } : {}),
    touch: typeof nav.maxTouchPoints === 'number' ? nav.maxTouchPoints > 0 : undefined,
  };
}

/** Where the visit came from: the referrer's HOST and the campaign tags — never a full URL, never a search's words. */
export function sourceOf(referrer = typeof document !== 'undefined' ? document.referrer : '', search = typeof window !== 'undefined' && window.location ? window.location.search : '') {
  let ref = '';
  try { ref = referrer ? new URL(referrer).hostname.replace(/^www\./, '').toLowerCase().slice(0, 60) : ''; } catch (_) { ref = ''; }
  let q;
  try { q = new URLSearchParams(search || ''); } catch (_) { q = new URLSearchParams(); }
  const tag = (k) => String(q.get(k) || '').slice(0, 40) || undefined;
  const utm_source = tag('utm_source'); const utm_medium = tag('utm_medium'); const utm_campaign = tag('utm_campaign');
  const paid = !!(q.get('fbclid') || q.get('gclid') || q.get('stylon_cid') || q.get('ttclid'));
  const from = `${utm_source || ''} ${ref}`.toLowerCase();
  const src = paid ? 'ad'
    : /instagram/.test(from) ? 'instagram' : /facebook|fb\.com/.test(from) ? 'facebook' : /google/.test(from) ? 'google'
    : /whatsapp/.test(from) ? 'whatsapp' : /tiktok/.test(from) ? 'tiktok' : ref ? 'other' : 'direct';
  return { src, ...(ref ? { ref } : {}), ...(utm_source ? { utm_source } : {}), ...(utm_medium ? { utm_medium } : {}), ...(utm_campaign ? { utm_campaign } : {}) };
}

/** The fields of the delivery form that hold something right now, by name — for "what they had filled when they left". */
export function filledFieldsIn(root = typeof document !== 'undefined' ? document : null) {
  if (!root || typeof root.querySelectorAll !== 'function') return [];
  const out = [];
  root.querySelectorAll('input, select, textarea').forEach((el) => {
    const name = fieldOfTarget(el);
    if (name && !name.includes(':') && isFilled(el) && !out.includes(name)) out.push(name);
  });
  return out.slice(0, 24);
}

/**
 * SECONDS THE PAGE WAS LOOKED AT, NOT SECONDS SINCE (2026-10-05). `checkout_left` counted from the
 * step's start by the wall clock, hidden or not, so a tab left open overnight reported 100,282 s and
 * 719,290 s on its step — numbers that say nothing about a shopper. This clock runs only while the
 * document is visible, restarts with each step, and is read capped: a reading at the cap can only
 * mean "stuck here", never "asleep".
 */
export const CHECKOUT_STEP_SECONDS_CAP = 1800;

export function visibleClock(doc = typeof document !== 'undefined' ? document : null, clock = () => Date.now()) {
  const isVisible = () => !doc || doc.visibilityState !== 'hidden';
  const state = { visibleMs: 0, visibleSince: isVisible() ? clock() : null };
  const pause = () => {
    if (state.visibleSince != null) { state.visibleMs += clock() - state.visibleSince; state.visibleSince = null; }
  };
  const resume = () => { if (state.visibleSince == null && isVisible()) state.visibleSince = clock(); };
  const onVisibility = () => { if (isVisible()) resume(); else pause(); };
  if (doc && typeof doc.addEventListener === 'function') doc.addEventListener('visibilitychange', onVisibility);
  return {
    /** A new step: the count starts again. */
    restart() { state.visibleMs = 0; state.visibleSince = isVisible() ? clock() : null; },
    /** Whole visible seconds so far, never above `cap`. */
    seconds(cap = CHECKOUT_STEP_SECONDS_CAP) {
      const ms = state.visibleMs + (state.visibleSince != null ? clock() - state.visibleSince : 0);
      return Math.min(cap, Math.max(0, Math.round(ms / 1000)));
    },
    stop() { if (doc && typeof doc.removeEventListener === 'function') doc.removeEventListener('visibilitychange', onVisibility); },
  };
}
